import {
  Timestamp,
  doc,
  type DocumentData,
  type DocumentReference,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { UserNotificationRefsConverter } from '@core/services/user-notifications/converters/user-notification-refs-converter'
import type { UserNotification } from '@domain/models/user-notifications'

function getDocRef(
  collectionPath: string,
  id: string,
): DocumentReference<DocumentData> {
  if (!firestore) {
    throw new Error('Firestore is not initialized.')
  }
  return doc(firestore, collectionPath, id)
}

function isReferenceLike(value: unknown): value is DocumentReference<DocumentData> {
  if (!value || typeof value !== 'object') {
    return false
  }
  if (!('id' in value) || !('path' in value)) {
    return false
  }

  const maybeRef = value as { id?: unknown; path?: unknown }
  return typeof maybeRef.id === 'string' && typeof maybeRef.path === 'string'
}

function refToId(value: unknown): string | undefined {
  if (!isReferenceLike(value)) {
    return undefined
  }
  return value.id
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function toOptionalTimestamp(value: Date | undefined): Timestamp | undefined {
  if (!value) {
    return undefined
  }
  return Timestamp.fromDate(value)
}

function stripNullish(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null),
  )
}

function toOptionalRef(
  collectionPath: string,
  value: string | undefined,
): DocumentReference<DocumentData> | undefined {
  const trimmedValue = value?.trim()
  if (!trimmedValue) {
    return undefined
  }
  return getDocRef(collectionPath, trimmedValue)
}

export class UserNotificationConverter {
  public static fromFirestore(
    uid: string,
    data: Record<string, unknown>,
  ): UserNotification {
    return {
      uid,
      title: toOptionalString(data['title']),
      description: toOptionalString(data['decription']) ?? toOptionalString(data['description']),
      createdTime: toOptionalDate(data['created_time']),
      read: typeof data['read'] === 'boolean' ? data['read'] : undefined,
      readTime: toOptionalDate(data['read_time']),
      activityRef: refToId(data['activity_ref']),
      supportTicketRef: refToId(data['support_ticket_ref']),
      userSlug: toOptionalString(data['user_slug']),
      notificationType: toOptionalString(data['notification_type']),
      sender: refToId(data['sender']),
      commentType: toOptionalString(data['comment_type']),
      notificationRefs: UserNotificationRefsConverter.fromFirestore(data['notification_refs']),
      likeType: toOptionalString(data['like_type']),
      caregiverNotifType: toOptionalString(data['caregiver_notif_type']),
    }
  }

  public static toFirestore(notification: Partial<UserNotification>): Record<string, unknown> {
    return stripNullish({
      title: notification.title,
      decription: notification.description,
      created_time: toOptionalTimestamp(notification.createdTime),
      read: notification.read,
      read_time: toOptionalTimestamp(notification.readTime),
      activity_ref: toOptionalRef('activities', notification.activityRef),
      support_ticket_ref: toOptionalRef('support_tickets', notification.supportTicketRef),
      user_slug: notification.userSlug,
      notification_type: notification.notificationType,
      sender: toOptionalRef('users', notification.sender),
      comment_type: notification.commentType,
      notification_refs: notification.notificationRefs
        ? UserNotificationRefsConverter.toFirestore(notification.notificationRefs)
        : undefined,
      like_type: notification.likeType,
      caregiver_notif_type: notification.caregiverNotifType,
      uid: notification.uid,
    })
  }
}
